import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuBanknote, LuBus, LuCheck, LuLoaderCircle } from 'react-icons/lu';
import { Field, TextInput } from '../../../components/form/FormControls';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import useSalaryPaymentText from './salaryPaymentText';

const TH = 'tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-500 dark:tw-text-slate-400';
const TD = 'tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-sm tw-tabular-nums';
const SMALL_INPUT = 'tw-h-9 tw-text-right tw-tabular-nums';

// Net = brüt − kesintiler + ek ödeme − ek kesinti (eski hesap)
const withNet = (payment) => {
  const baseNetSalary = payment.grossSalary - payment.totalDeductions;
  return { ...payment, netSalary: baseNetSalary + payment.additionalPayment - payment.additionalDeduction };
};

/**
 * Toplu Maaş Ödeme Önizleme: the month's unpaid salaries with an extra
 * payment and deduction per person, "Yol Öde" (the same extra payment for
 * everyone) and the totals; confirming writes every payment at once.
 */
const BulkPaymentModal = ({ initialData, period, onClose, onConfirm }) => {
  const { language, label, formatMoney } = useSalaryPaymentText();
  const [paymentPreviewData, setPaymentPreviewData] = useState(initialData);
  const [showYolOde, setShowYolOde] = useState(false);
  const [yolUcretiAmount, setYolUcretiAmount] = useState('');
  const [paying, setPaying] = useState(false);

  const handleAdditionalChange = (employeeId, field, value) => {
    const numValue = parseFloat(value) || 0;
    setPaymentPreviewData((prevData) => prevData.map((payment) => (payment.id === employeeId ? withNet({ ...payment, [field]: numValue }) : payment)));
  };

  const openYolOde = () => {
    setYolUcretiAmount('');
    setShowYolOde(true);
  };

  const handleYolOdeConfirm = () => {
    const amount = parseFloat(yolUcretiAmount) || 0;
    if (amount <= 0) {
      setShowYolOde(false);
      return;
    }
    setPaymentPreviewData((prevData) => prevData.map((payment) => withNet({ ...payment, additionalPayment: amount })));
    setShowYolOde(false);
  };

  const handleConfirm = async () => {
    setPaying(true);
    const ok = await onConfirm(paymentPreviewData);
    setPaying(false);
    if (ok) onClose();
  };

  const sum = (key) => paymentPreviewData.reduce((total, payment) => total + payment[key], 0);
  const totals = {
    grossSalary: sum('grossSalary'),
    totalDeductions: sum('totalDeductions'),
    additionalPayment: sum('additionalPayment'),
    additionalDeduction: sum('additionalDeduction'),
    netSalary: sum('netSalary'),
    totalEmployerCost: sum('totalEmployerCost'),
  };

  const amountInput = (payment, field) => (
    <TextInput
      type="number"
      inputMode="decimal"
      min="0"
      step="0.01"
      value={payment[field]}
      onChange={(event) => handleAdditionalChange(payment.id, field, event.target.value)}
      placeholder="0.00"
      aria-label={`${label(`bulk.columns.${field}`)} · ${payment.employeeName} ${payment.employeeSurname}`}
      className={SMALL_INPUT}
    />
  );

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        size="xl"
        dismissible={!paying}
        title={label('bulk.title')}
        subtitle={label('bulk.subtitle', { period })}
        closeLabel={label('common.cancel')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30" size="xs">
            <LuBanknote aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={(
          <>
            <Button variant="ghost" onClick={onClose} disabled={paying}>{label('common.cancel')}</Button>
            <Button onClick={openYolOde} disabled={paying}>
              <LuBus aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('bulk.yolOde')}
            </Button>
            <Button variant="success" onClick={handleConfirm} disabled={paying || paymentPreviewData.length === 0}>
              {paying ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" />}
              {label('bulk.confirm')}
            </Button>
          </>
        )}
      >
        <div className="tw-space-y-4">
          <div className="tw-grid tw-grid-cols-3 tw-gap-2 sm:tw-gap-3">
            {[
              ['count', paymentPreviewData.length.toLocaleString()],
              ['totalNet', formatMoney(totals.netSalary)],
              ['totalCost', formatMoney(totals.totalEmployerCost)],
            ].map(([key, value]) => (
              <div key={key} className="tw-min-w-0 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3 sm:tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                <p className="tw-truncate tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-500 dark:tw-text-slate-400">{label(`bulk.${key}`)}</p>
                <p className="tw-mt-1 tw-truncate tw-text-base tw-font-semibold tw-tabular-nums tw-text-slate-900 sm:tw-text-xl dark:tw-text-white">{value}</p>
              </div>
            ))}
          </div>

          <p className="tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{label('bulk.note')}</p>

          {/* Phones: one card per person */}
          <ul className="tw-space-y-2.5 md:tw-hidden">
            {paymentPreviewData.map((payment) => (
              <li key={payment.id} className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
                  <p className="tw-min-w-0 tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{payment.employeeName} {payment.employeeSurname}</p>
                  <p className="tw-shrink-0 tw-text-base tw-font-semibold tw-tabular-nums tw-text-emerald-700 dark:tw-text-emerald-300">{formatMoney(payment.netSalary)}</p>
                </div>
                <p className="tw-mt-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                  {label('bulk.cardLine', { gross: formatMoney(payment.grossSalary), deductions: formatMoney(payment.totalDeductions) })}
                </p>
                <div className="tw-mt-2.5 tw-grid tw-grid-cols-2 tw-gap-2">
                  <label className="tw-space-y-1">
                    <span className="tw-block tw-text-[11px] tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{label('bulk.columns.additionalPayment')}</span>
                    {amountInput(payment, 'additionalPayment')}
                  </label>
                  <label className="tw-space-y-1">
                    <span className="tw-block tw-text-[11px] tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{label('bulk.columns.additionalDeduction')}</span>
                    {amountInput(payment, 'additionalDeduction')}
                  </label>
                </div>
              </li>
            ))}
          </ul>

          {/* Wider screens: the table with totals */}
          <div className="tw-hidden tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 md:tw-block dark:tw-border-white/10 glass-scrollbar">
            <table className="tw-w-full tw-border-collapse">
              <thead className="tw-bg-slate-50/80 dark:tw-bg-white/[0.03]">
                <tr>
                  <th className={TH}>{label('bulk.columns.employee')}</th>
                  <th className={`${TH} tw-text-right`}>{label('bulk.columns.grossSalary')}</th>
                  <th className={`${TH} tw-text-right`}>{label('bulk.columns.totalDeductions')}</th>
                  <th className={`${TH} tw-text-right`}>{label('bulk.columns.additionalPayment')}</th>
                  <th className={`${TH} tw-text-right`}>{label('bulk.columns.additionalDeduction')}</th>
                  <th className={`${TH} tw-text-right`}>{label('bulk.columns.netSalary')}</th>
                  <th className={`${TH} tw-text-right`}>{label('bulk.columns.totalEmployerCost')}</th>
                </tr>
              </thead>
              <tbody>
                {paymentPreviewData.map((payment) => (
                  <tr key={payment.id} className="tw-border-t tw-border-slate-200/70 dark:tw-border-white/[0.06]">
                    <td className={`${TD} tw-font-semibold tw-text-slate-900 dark:tw-text-white`}>{payment.employeeName} {payment.employeeSurname}</td>
                    <td className={`${TD} tw-text-right tw-text-slate-700 dark:tw-text-slate-200`}>{formatMoney(payment.grossSalary)}</td>
                    <td className={`${TD} tw-text-right tw-text-rose-600 dark:tw-text-rose-300`}>{formatMoney(payment.totalDeductions)}</td>
                    <td className={`${TD} tw-text-right`}><span className="tw-ml-auto tw-block tw-w-28">{amountInput(payment, 'additionalPayment')}</span></td>
                    <td className={`${TD} tw-text-right`}><span className="tw-ml-auto tw-block tw-w-28">{amountInput(payment, 'additionalDeduction')}</span></td>
                    <td className={`${TD} tw-text-right tw-font-semibold tw-text-emerald-700 dark:tw-text-emerald-300`}>{formatMoney(payment.netSalary)}</td>
                    <td className={`${TD} tw-text-right tw-text-slate-700 dark:tw-text-slate-200`}>{formatMoney(payment.totalEmployerCost)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="tw-bg-slate-50/80 dark:tw-bg-white/[0.03]">
                <tr className="tw-border-t-2 tw-border-slate-200 dark:tw-border-white/10">
                  <td className={`${TD} tw-font-bold tw-uppercase tw-text-slate-900 dark:tw-text-white`}>{label('bulk.total')}</td>
                  <td className={`${TD} tw-text-right tw-font-semibold tw-text-slate-900 dark:tw-text-white`}>{formatMoney(totals.grossSalary)}</td>
                  <td className={`${TD} tw-text-right tw-font-semibold tw-text-rose-600 dark:tw-text-rose-300`}>{formatMoney(totals.totalDeductions)}</td>
                  <td className={`${TD} tw-text-right tw-font-semibold tw-text-emerald-700 dark:tw-text-emerald-300`}>{formatMoney(totals.additionalPayment)}</td>
                  <td className={`${TD} tw-text-right tw-font-semibold tw-text-rose-600 dark:tw-text-rose-300`}>{formatMoney(totals.additionalDeduction)}</td>
                  <td className={`${TD} tw-text-right tw-font-bold tw-text-slate-900 dark:tw-text-white`}>{formatMoney(totals.netSalary)}</td>
                  <td className={`${TD} tw-text-right tw-font-semibold tw-text-slate-900 dark:tw-text-white`}>{formatMoney(totals.totalEmployerCost)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showYolOde}
        onClose={() => setShowYolOde(false)}
        size="md"
        title={label('yol.title')}
        subtitle={label('yol.subtitle')}
        closeLabel={label('common.cancel')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30" size="xs">
            <LuBus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setShowYolOde(false)}>{label('common.cancel')}</Button>
            <Button variant="primary" type="submit" form="yol-ode-form">{label('yol.apply')}</Button>
          </>
        )}
      >
        <form
          id="yol-ode-form"
          onSubmit={(event) => {
            event.preventDefault();
            handleYolOdeConfirm();
          }}
        >
          <Field label={label('yol.amount')} htmlFor="yol-ucreti" hint={label('yol.hint', { count: paymentPreviewData.length })}>
            <TextInput
              id="yol-ucreti"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={yolUcretiAmount}
              onChange={(event) => setYolUcretiAmount(event.target.value)}
              placeholder={label('yol.placeholder')}
              autoFocus
              className="tw-font-semibold tw-tabular-nums"
            />
          </Field>
        </form>
      </Modal>
    </>
  );
};

BulkPaymentModal.propTypes = {
  initialData: PropTypes.arrayOf(PropTypes.object).isRequired,
  period: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default BulkPaymentModal;
